import React, { useMemo } from 'react';
import { Card, Typography, Space } from '../../../components';
import { ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { getGiBfromKiB } from '../../../lib/helpers';

const { Text } = Typography;

const PerformanceMetrics = React.memo(({ source }) => {
    const chartData = useMemo(() => {
        if (!source || source.collection_type !== 'vm' || !source.attributes?.vm_perf_metrics) {
            return null;
        }

        const { vm_perf_metrics } = source.attributes;
        const { timestamps, ram, cpu } = vm_perf_metrics;

        // Validate that we have all required arrays and they're the same length
        if (
            !timestamps ||
            !ram ||
            !cpu ||
            timestamps.length !== ram.length ||
            timestamps.length !== cpu.length ||
            timestamps.length === 0
        ) {
            return null;
        }

        const processedData = [];
        for (let i = 0; i < timestamps.length; i++) {
            // Format timestamp - YYYY-MM-DD HH:MM format
            const dt = new Date(timestamps[i]);
            const formattedTime =
                dt.getFullYear() +
                '-' +
                String(dt.getMonth() + 1).padStart(2, '0') +
                '-' +
                String(dt.getDate()).padStart(2, '0') +
                ' ' +
                String(dt.getHours()).padStart(2, '0') +
                ':' +
                String(dt.getMinutes()).padStart(2, '0');

            processedData.push({
                time: formattedTime,
                // Use getGiBfromKiB for RAM conversion
                ramGiB: getGiBfromKiB(ram[i] || 0),
                // Use Math.ceil(c / 100) for CPU
                cpuPercent: Math.ceil((cpu[i] || 0) / 100),
            });
        }

        return processedData;
    }, [source]);

    const recommendedSizing = useMemo(() => {
        if (!chartData || chartData.length === 0 || !source?.attributes) return null;

        const attributes = source.attributes;
        // Get recommended CPU - default to processor count or 1
        let recommended_cpu = attributes.cpu ? (attributes.cpu.processors ? attributes.cpu.processors.length : 1) : 1;
        // Get recommended RAM - start with total memory
        let recommended_ram_gb = getGiBfromKiB(attributes.memory.total_kb);
        // Override with performance metrics if available
        if (attributes.vm_perf_metrics?.ram_95th_percentile) {
            recommended_ram_gb = getGiBfromKiB(attributes.vm_perf_metrics.ram_95th_percentile);
        }
        if (attributes.vm_perf_metrics?.recommended_cpu) {
            recommended_cpu = attributes.vm_perf_metrics.recommended_cpu;
        }

        return {
            cpu: recommended_cpu,
            ram: recommended_ram_gb,
        };
    }, [chartData, source]);

    if (!chartData) {
        return null;
    }

    return (
        <Card title="Performance Metrics" size="small">
            <Space orientation="vertical" style={{ width: '100%' }}>
                <ResponsiveContainer width="100%" height={300}>
                    <ComposedChart data={chartData} margin={{ top: 40, right: 30, left: 20, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="time" angle={-45} textAnchor="end" height={60} fontSize={11} />
                        <YAxis
                            yAxisId="cpu"
                            orientation="left"
                            label={{ value: 'CPU', angle: -90, position: 'inside' }}
                            domain={[0, 100]}
                        />
                        <YAxis
                            yAxisId="ram"
                            orientation="right"
                            label={{ value: 'RAM', angle: 90, position: 'inside' }}
                            domain={[0, 'dataMax']}
                        />
                        <Tooltip
                            formatter={(value, name) => {
                                return [name.trim() === 'CPU' ? `${value} %` : `${value} GiB`, name];
                            }}
                            labelFormatter={(label) => label}
                        />
                        <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        <Line
                            yAxisId="cpu"
                            type="monotone"
                            dataKey="cpuPercent"
                            stroke="#ff0000"
                            name="CPU"
                            strokeWidth={1}
                            dot={false}
                        />
                        <Line
                            yAxisId="ram"
                            type="monotone"
                            dataKey="ramGiB"
                            stroke="#0133cc"
                            name="RAM"
                            strokeWidth={1}
                            dot={false}
                        />
                    </ComposedChart>
                </ResponsiveContainer>

                {recommendedSizing && (
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        <strong>Recommended sizing:</strong> CPU: {recommendedSizing.cpu} %, RAM:{' '}
                        {recommendedSizing.ram} GiB
                    </Text>
                )}
            </Space>
        </Card>
    );
});

PerformanceMetrics.displayName = 'PerformanceMetrics';

export default PerformanceMetrics;
